import axios, { AxiosInstance, AxiosError } from "axios";
import { Buffer } from "buffer";
class VTRS {
  private axios: AxiosInstance;
  public config: VTRSConfig;
  public token: string | undefined;
  public parsedToken: any | undefined;
  public resources: Resources | undefined;
  public userInfo: any | undefined;
  public authenticated: boolean;
  public ssoIframeExists: boolean;
  private tokenTTL: Worker | null;

  constructor(config: VTRSConfig) {
    this.authenticated = false;
    this.tokenTTL = null;
    this.ssoIframeExists = false;
    this.config = config;
    this.axios = axios.create();
    this.axios.defaults.baseURL = config.apiURL;

    if (window.parent !== window) {
      window.addEventListener("message", async event => {
        if (this.config.loginURL === this.config.baseSite) {
          if (event.data.ssoMsg === "Logout") {
            window.localStorage.setItem("ssoLogout", "true");
          }
        }

        if (event.data.token) {
          window.localStorage.setItem("ssoData", JSON.stringify(event.data));

          if (this.config.loginURL === this.config.baseSite) {
            parent.postMessage(
              {
                ssoMsg: "Token Updated"
              },
              "*"
            );
          } else {
            parent.postMessage(
              {
                url: this.config.baseSite
              },
              "*"
            );
          }
        }
      });
      if (this.config.loginURL !== this.config.baseSite) {
        parent.postMessage(
          {
            ssoMsg: "Ready For Token"
          },
          "*"
        );
      }
    } else if (window.parent === window) {
      window.addEventListener("message", async event => {
        if (
          event.data.ssoMsg === "Ready For Token" ||
          event.data.ssoMsg === "Update Token"
        ) {
          const iframe = window.document.getElementById(
            "iframe"
          ) as HTMLIFrameElement | null;
          if (iframe) {
            iframe.contentWindow?.postMessage(this.userInfo, iframe.src);
          }
        }

        if (event.data.ssoMsg === "Logout") {
          await this.logout(true);
          this.redirect(true);
        }

        if (event.data.token) {
          const iframe = window.document.getElementById(
            "iframe"
          ) as HTMLIFrameElement | null;

          await this.handleToken(
            event.data,
            false,
            iframe !== null || this.config.loginURL === this.config.baseSite
          );

          if (iframe) {
            window.localStorage.setItem("ssoData", JSON.stringify(event.data));

            iframe.contentWindow?.postMessage(
              {
                ssoMsg: "Token Updated"
              },
              iframe.src
            );
          }
        }
      });
    }

    if (this.config.loginURL === this.config.baseSite) {
      window.addEventListener(
        "storage",
        event => {
          if (event.key === "ssoData" && event.newValue) {
            const ssoData = JSON.parse(event.newValue);
            parent.postMessage(ssoData, "*");
          }

          if (event.key === "ssoLogout") {
            parent.postMessage(
              {
                ssoMsg: "Logout"
              },
              "*"
            );
          }
        },
        false
      );
    }

    this.createSSOIframe();
  }

  async init(): Promise<boolean> {
    const ssoDataString = localStorage.getItem("ssoData");
    let ssoData: any = {};
    let valid = false;

    if (ssoDataString) {
      ssoData = JSON.parse(ssoDataString);
    }

    if (ssoData.token && window === window.parent) {
      valid = await this.handleToken(ssoData, true);

      if (!valid) {
        await this.logout(true);
      }
    }

    return valid;
  }

  async login(loginInfo: LoginInfo) {
    try {
      const loginResp = (await this.axios.post("/login", loginInfo)).data;
      await this.handleToken(loginResp);
    } catch (e) {
      this.errorHandler(e as AxiosError);
    }
  }

  async refresh(): Promise<boolean> {
    let success = false;

    try {
      const refreshResp = (await this.axios.get("/auth/refresh")).data;
      success = await this.handleToken(refreshResp, false, true);
    } catch (e) {
      this.errorHandler(e as AxiosError, true);
    }

    return success;
  }

  async logout(override = false) {
    if (this.config.loginURL !== this.config.baseSite && !override) {
      window.location.href = `${this.config.loginURL}#/logout`;
    } else {
      if (!override) {
        await this.axios
          .post("/logout")
          .catch((error: AxiosError) => this.errorHandler(error, true));
        localStorage.setItem("ssoLogout", "true");
      }
      localStorage.removeItem("ssoData");
      localStorage.removeItem("ssoLogout");
      this.tokenTTL?.terminate();
      this.tokenTTL = null;
      this.axios.defaults.headers.common.Authorization = "";
      this.authenticated = false;
      this.token = undefined;
      this.parsedToken = undefined;
      this.userInfo = undefined;
      this.resources = undefined;
      this.ssoIframeExists = false;
    }
  }

  async saveCardOrder(cardOrder: { permission: number }[]): Promise<boolean> {
    let success = false;

    try {
      await this.axios.post("/sso/cards/order", cardOrder);
      success = true;
    } catch (e) {
      this.errorHandler(e as AxiosError);
    }

    return success;
  }

  redirect(forbidden?: boolean) {
    if (
      (this.config.loginURL !== this.config.baseSite &&
        window === window.parent) ||
      forbidden === true
    ) {
      window.location.href = `${this.config.loginURL}#/login`;
    }
  }

  createSSOIframe(): HTMLIFrameElement | null {
    if (!window.location.href.includes(this.config.loginURL)) {
      this.ssoIframeExists = true;
      const iframe = window.document.createElement("iframe");
      iframe.setAttribute("id", "iframe");
      iframe.style.display = "none";
      iframe.src = this.config.loginURL;
      return window.document.body.appendChild(iframe);
    }
    return null;
  }

  openWebsite(siteURL: string, newTab = false): void {
    let iframe = document.getElementById("iframe");

    if (iframe) {
      document.body.removeChild(iframe);
    }

    iframe = document.createElement("iframe");
    iframe.setAttribute("id", "iframe");

    if (newTab) {
      iframe.setAttribute("newTab", "true");
    }

    iframe.style.display = "none";
    (iframe as HTMLIFrameElement).src = siteURL;
    document.body.appendChild(iframe);
  }

  // MDN Article for Inline Web Workers: https://developer.mozilla.org/en-US/docs/Web/API/Web_Workers_API/Using_web_workers#embedded_workers
  createWorker(fn: Function, functionName: string) {
    const fnFixed = fn
      .toString()
      .replace("function()", `function ${functionName}()`);
    const blob = new Blob([fnFixed, `${functionName}();`], {
      type: "text/javascript"
    });
    const url = URL.createObjectURL(blob);

    return new Worker(url);
  }

  private getIconName(program: string): string {
    let name = program;

    name = name.toLowerCase();
    name = name.replaceAll(" ", "_");

    return name;
  }

  private async handleToken(
    authResp: Auth,
    checkDate = false,
    isRefresh = false
  ): Promise<boolean> {
    const parsedToken = this.parseToken(authResp.token);
    let success = false;

    if (
      this.isSSO(parsedToken) && checkDate === true
        ? Date.now() <= parsedToken.exp * 1000
        : true
    ) {
      this.tokenTTL?.terminate();
      this.tokenTTL = null;

      this.token = authResp.token;
      this.parsedToken = this.parseToken(authResp.token);
      if (!isRefresh) {
        this.userInfo = authResp;
      } else {
        this.userInfo.token = authResp.token;
      }

      if (this.ssoIframeExists) {
        window.postMessage(
          {
            ssoMsg: "Update Token"
          },
          window.location.href
        );
      }

      success = await this.createTokenTTL(isRefresh);
      if (success) {
        if (isRefresh) {
          const ssoData = window.localStorage.getItem("ssoData");
          if (ssoData) {
            const data = JSON.parse(ssoData) as Auth;
            data.token = authResp.token;
            window.localStorage.setItem("ssoData", JSON.stringify(data));
          }
        } else {
          window.localStorage.setItem("ssoData", JSON.stringify(authResp));
        }
      }
    }

    return success;
  }

  private async populateData(isRefresh = false): Promise<void> {
    if (this.token) {
      this.axios.defaults.headers.common.Authorization = `Bearer ${this.token}`;

      if (!isRefresh) {
        const resources = (await this.axios.get("/sso/cards")).data;

        resources.forEach((card: any) => {
          card.icon =
            this.config.icons[this.getIconName(card.name)] ??
            this.config.icons["default"];
        });

        this.resources = resources;
      }

      this.authenticated = true;
    }
  }

  private parseToken(token: string): any {
    return JSON.parse(Buffer.from(token.split(".")[1], "base64").toString());
  }

  private async createTokenTTL(isRefresh = false): Promise<boolean> {
    let success = false;
    let tokenExpiresTime =
      this.parsedToken.exp * 1000 - Date.now() - this.config.inactivityTimer;

    try {
      await this.populateData(isRefresh);

      const startTimer = function() {
        setInterval(() => {
          self.postMessage(1000);
        }, 1000);
      };

      this.tokenTTL = this.createWorker(startTimer, "startTimer");

      this.tokenTTL.onmessage = e => {
        if (tokenExpiresTime <= 0) {
          window.postMessage(
            {
              ssoMsg: "Token Expired"
            },
            "*"
          );
          this.tokenTTL?.terminate();
          this.tokenTTL = null;
        }
        tokenExpiresTime -= e.data;
      };

      success = true;
    } catch (e) {
      this.errorHandler(e as AxiosError);
    }

    return success;
  }

  private isSSO(parsedToken: any): boolean {
    return parsedToken ? parsedToken.per && parsedToken.per.sso : false;
  }

  private errorHandler(error: AxiosError, isRefresh = false): void {
    window.console.error(error.message);

    if (
      error.response?.status === 401 ||
      error.response?.status === 403 ||
      isRefresh
    ) {
      if (this.config.loginURL === this.config.baseSite) {
        window.localStorage.setItem("ssoLogout", "true");

        if (window === window.parent) {
          this.logout(true);
        }
      } else {
        const iframe = window.document.getElementById(
          "iframe"
        ) as HTMLIFrameElement | null;
        if (iframe) {
          iframe.contentWindow?.postMessage({ ssoMsg: "Logout" }, iframe.src);
        }

        if (!isRefresh) {
          this.logout(true);
          this.redirect(true);
        }
      }
    }
  }
}

interface Resources {
  [key: string]: any;
}

interface Icons {
  [key: string]: string;
}

interface LoginInfo {
  username: string;
  password: string;
}

interface Auth {
  userID: number;
  username: string;
  userFullName: string;
  emailAddress: string;
  partnerID: number;
  partnerName: string;
  token: string;
  passwordExpiresAt: number;
  featureFlags: {
    [key: string]: boolean;
  };
}

interface VTRSConfig {
  apiURL: string;
  loginURL: string;
  baseSite: string;
  icons: Icons;
  inactivityTimer: number;
}

declare module "vue/types/vue" {
  export interface Vue {
    $vtrs: VTRS;
  }

  interface VueConstructor {
    $vtrs: VTRS;
  }
}

export { VTRS, VTRSConfig, LoginInfo, Resources };
